import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader, EmptyState, LoadingRows, ErrorBox } from "@/components/common";
import { CompanyLogo } from "@/components/common/CompanyLogo";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { MapPin, Factory, ArrowRight, UserPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
const SOURCE_LABEL = {
  lead_capture: "Lead capture", manual: "Added manually", discovery: "Discovery", import: "Imported",
};

function QueueCard({ a, members, onAssigned }) {
  const navigate = useNavigate();
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (!owner) { toast.error("Choose who should own this lead."); return; }
    setBusy(true);
    try {
      await api.post(`/accounts/${a.id}/reassign`, { new_owner_id: owner, handover_note: "Assigned from the manager queue" });
      toast.success(`Assigned ${a.name} to ${members.find((m) => m.user_id === owner)?.name || "owner"}`);
      onAssigned(a.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="panel p-4 flex flex-col gap-3" data-testid={`queue-card-${a.id}`}>
      <div className="flex items-start gap-3">
        <CompanyLogo domain={a.domain} website={a.website} name={a.name} size="lg" />
        <div className="min-w-0 flex-1">
          <button className="text-left font-medium truncate hover:text-primary flex items-center gap-1" onClick={() => navigate(`/accounts?open=${a.id}`)} data-testid={`queue-open-${a.id}`}>
            <span className="truncate">{a.name}</span><ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </button>
          <div className="meta flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
            {a.industry && <span className="inline-flex items-center gap-1"><Factory className="h-3 w-3" />{a.industry}</span>}
            {(a.location || a.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[a.location, a.country].filter(Boolean).join(", ")}</span>}
          </div>
        </div>
        <span className="chip chip-warning shrink-0" data-testid={`queue-badge-${a.id}`}>Needs owner</span>
      </div>

      {a.summary && <p className="text-sm text-muted-foreground line-clamp-2">{a.summary}</p>}

      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-muted-foreground">
        <span>{SOURCE_LABEL[a.source] || a.source || "Lead"}{a.created_at && <> · {fmtDate(a.created_at)}</>}</span>
      </div>

      <div className="flex items-center gap-2 border-t border-border pt-3">
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger className="flex-1" data-testid={`queue-owner-select-${a.id}`}><SelectValue placeholder="Assign to…" /></SelectTrigger>
          <SelectContent>
            {members.length === 0 && <SelectItem value="none" disabled>No eligible salespeople</SelectItem>}
            {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={assign} disabled={busy || !owner} data-testid={`queue-assign-${a.id}`}>
          <UserPlus className="h-4 w-4 mr-1.5" />{busy ? "Assigning…" : "Assign"}<ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

export default function ManagerQueuePage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);

  const load = useCallback(() => {
    setError(null);
    api.get("/accounts/review-queue").then((r) => setRows(r.data || [])).catch((e) => { setError(errMsg(e)); setRows([]); });
  }, []);

  useEffect(() => {
    load();
    api.get("/org/members").then((r) => setMembers((r.data || []).filter((m) => m.status === "active" && m.is_sales_eligible))).catch(() => {});
  }, [load]);

  const onAssigned = (id) => setRows((rs) => (rs || []).filter((a) => a.id !== id));

  return (
    <div className="page max-w-5xl">
      <PageHeader title="Manager queue" subtitle="Captured and unassigned leads waiting for an owner. Assign each one to a salesperson to get it moving." />

      {error && <ErrorBox error={error} />}
      {rows === null && !error && <LoadingRows rows={4} />}
      {rows && rows.length === 0 && !error && (
        <EmptyState title="Nothing waiting" text="Every lead has an owner. New captured leads that can't be auto-assigned will appear here." testId="queue-empty" />
      )}
      {rows && rows.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="queue-grid">
          {rows.map((a) => <QueueCard key={a.id} a={a} members={members} onAssigned={onAssigned} />)}
        </div>
      )}
    </div>
  );
}
